import { Link, useLocation } from 'react-router-dom';
import {
  Barbell,
  ChartLine,
  ClipboardText,
  House,
  UsersThree,
  type Icon,
} from '@phosphor-icons/react';
import { useAuth } from '@/core/AuthContext';
import { Avatar, ThemeToggle } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';

export function NavBar() {
  const { userData } = useAuth();
  const location = useLocation();

  const isTrainer = userData?.role === 'trainer';

  const navLinks = isTrainer
    ? [
        { to: '/trainer', label: 'Exercises', icon: Barbell },
        { to: '/trainer/workouts', label: 'Workouts', icon: ClipboardText },
        { to: '/trainer/clients', label: 'Clients', icon: UsersThree },
      ]
    : [
        { to: '/client', label: 'Dashboard', icon: House },
        { to: '/client/train', label: 'Train', icon: Barbell },
        { to: '/client/progress', label: 'Progress', icon: ChartLine },
      ];

  const isActive = (path: string) => {
    if (path === '/trainer' || path === '/client') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-gray-200 dark:border-white/10 safe-top" data-testid="top-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to={isTrainer ? '/trainer' : '/client'}
            className="flex items-center gap-2 font-bold text-lg"
            data-testid="nav-home-link"
          >
            <img
              src="/favicon-32.png"
              alt="Start Grounded"
              className="h-8 w-8 rounded-lg shadow-sm ring-1 ring-black/5 dark:ring-white/10"
            />
            <span className="hidden sm:inline bg-gradient-to-r from-primary-600 to-primary-500 dark:from-primary-400 dark:to-primary-500 bg-clip-text text-transparent">
              Start Grounded
            </span>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const Icon = link.icon as Icon;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}-link`}
                  className={cn(
                    'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                    isActive(link.to)
                      ? 'bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400'
                      : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-white/5'
                  )}
                >
                  <Icon size={18} weight="bold" />
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link
              to={isTrainer ? '/trainer/settings' : '/client/settings'}
              className="flex items-center hover:opacity-80 transition-opacity"
              aria-label="Settings"
              data-testid="nav-user-menu-link"
            >
              <Avatar
                size="md"
                fallback={userData?.displayName || 'User'}
                alt={userData?.displayName || 'User'}
              />
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
